import React, { useEffect } from 'react'
import {
  makeStyles,
  createStyles,
  Typography,
  Grid,
  Container,
  useTheme,
  useMediaQuery,
} from '@material-ui/core'
import clsx from 'clsx'
import Link from '../Link/Link'
import { connect } from 'react-redux'
import { getPackTaxons } from '../../redux/actions/packs'
import { getTaxons } from '../../redux/actions/taxons'
import { Swiper, SwiperSlide } from 'swiper/react'
import { SwiperOptions } from 'swiper/types/swiper-options'
import SwiperCore, { Pagination, Navigation, A11y } from 'swiper'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { columnLayout, columnSpacing, thumbnailRatio } from '../../utils/utils'
import Button from '../Button/Button'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons'
import Skeleton from '@material-ui/lab/Skeleton'
import CMSText, { isPrismicText, prismicText } from '../Global/CMSText'
import { DiscoverCard } from './DiscoverCard'
import {
  PrismicHomePage,
  PrismicHomePageData,
} from '../../interfaces/common/prismicHomePage'
import { RootState } from '../../redux/reducers/rootReducer'
import { PrismicGeneralData } from '../../interfaces/common/prismicGeneralConfig'

SwiperCore.use([Navigation, Pagination, A11y])

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      textAlign: 'left',
      padding: theme.spacing(6, 0),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0),
      },
      '& .swiper-slide': {
        marginBottom: theme.spacing(4),
      },
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
    },
    title: {
      margin: theme.spacing(2),
    },
    cardLink: {
      display: 'block',
    },
    taxonTitle: {
      padding: theme.spacing(2, 0),
      textAlign: 'center',
    },
    mainTitle: {
      width: '100%',
      textAlign: 'center',
      padding: theme.spacing(4, 0),
    },
    subtitle: {
      padding: theme.spacing(2, 0),
    },
    background: {
      //background: `linear-gradient(110deg, ${theme.palette.common.white} 60%, ${theme.palette.primaryTransparent.light} 60%)`,
    },
    fullwidth: {
      position: 'relative',
      width: '100vw',
      transform: 'translateX(-50%) translateY(0)',
      left: '50%',
    },
    col: {},
    md5: {
      [theme.breakpoints.up('lg')]: {
        maxWidth: '20%',
        flexBasis: '20%',
      },
    },
    swiperArrows: {
      position: 'relative',
      [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(0, 6),
      },
    },
    swiperNoArrows: {},
    swiperArrow: {
      zIndex: 2,
      padding: 0,
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      minWidth: 32,
      width: 32,
      height: 32,
      background: theme.palette.common.white,
      '&[disabled]': {
        opacity: 0.3,
        cursor: 'default',
      },
      '&#swiper-taxon__prev, &#swiper-taxonPack__prev, &#swiper-products__prev, &#swiper-taxon_2__prev, &#swiper-taxonPack_2__prev, &#swiper-products_2__prev':
        {
          left: theme.spacing(1),
        },
      '&#swiper-taxon__next, &#swiper-taxonPack__next, &#swiper-products__next, &#swiper-taxon_2__next, &#swiper-taxonPack_2__next, &#swiper-products_2__next':
        {
          right: theme.spacing(1),
        },
    },
    skeletonImg: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      borderRadius: theme.shape.borderRadius,
    },
    footer: {
      textAlign: 'center',
    },
  })
)

const mapStateToProps = (state: RootState) => ({
  taxons: state.taxons,
  taxonPack: state.taxonPack,
})

const mapDispatchToProps = {
  getTaxons: getTaxons,
  getPackTaxons: getPackTaxons,
}

type Tprops = {
  homePage?: PrismicHomePage
  settings?: PrismicGeneralData
  getTaxons: any
  getPackTaxons: any
  taxons: any
  t?: any
  taxonLimit?: any
  isPack: boolean
  taxonPack: any
  universeSuffix?: string
}

const Discover = ({
  homePage,
  settings,
  getTaxons,
  getPackTaxons,
  isPack,
  taxonLimit,
  taxonPack,
  taxons,
  universeSuffix = '',
}: Tprops) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isMobileColumn = useMediaQuery(theme.breakpoints.down('xs'))

  // Helper function to get property with suffix
  const getProp = (propName: string) => {
    const propKey = `${propName}${universeSuffix}` as keyof PrismicHomePageData
    return homePage?.data?.[propKey]
  }

  const aspectRatio = thumbnailRatio(
    isMobileColumn
      ? (getProp('mobile_thumbnail_ratio') as any)
      : settings?.thumbnail_ratio
  )
  const thumbWidth = settings?.medium_image_size
  const gridItemWidth = columnLayout(getProp('classic_grid_columns') as string)
  const gridSpacing = columnSpacing(getProp('carousel_cards_spacing') as string)
  const isArrows = getProp('univers_carousel_arrows') as boolean
  const isFiveColumns = gridItemWidth === '20%'
  const { trackEvent } = useMatomo()
  const isTaxonsChildren = taxons?.items?.[0]?.children?.length > 0
  const carouselProducts = getProp('carousel_products') as any[]

  const swiperParamsMobile: SwiperOptions = {
    centeredSlides: true,
    spaceBetween: 0,
    loop: true,
    slidesPerView: theme?.settings?.productsSwiperSlidesPerView || 1,
  }

  const swiperParamsDesktop: SwiperOptions = {
    spaceBetween: theme.spacing(gridSpacing),
    centeredSlides: false,
    loop: false,
  }

  const swiperParamsTaxonsM: SwiperOptions = {
    ...swiperParamsMobile,
    breakpoints: {
      768: {
        slidesPerView: 2,
      },
      1024: {
        slidesPerView:
          isTaxonsChildren && taxons.items[0].children.length < 4
            ? taxons.items[0].children.length
            : 4,
      },
      1680: {
        slidesPerView:
          isTaxonsChildren && taxons.items[0].children.length < 6
            ? taxons.items[0].children.length
            : 6,
      },
    },
    navigation: {
      nextEl: `#swiper-taxon${universeSuffix}__next`,
      prevEl: `#swiper-taxon${universeSuffix}__prev`,
    },
  }

  const swiperParamsTaxonsD: SwiperOptions = {
    ...swiperParamsDesktop,
    breakpoints: {
      768: {
        slidesPerView: 2,
      },
      1024: {
        slidesPerView:
          isTaxonsChildren && taxons.items[0].children.length < 4
            ? taxons.items[0].children.length
            : 4,
      },
      1680: {
        slidesPerView:
          isTaxonsChildren && taxons.items[0].children.length < 6
            ? taxons.items[0].children.length
            : 6,
      },
    },
    navigation: {
      nextEl: `#swiper-taxon${universeSuffix}__next`,
      prevEl: `#swiper-taxon${universeSuffix}__prev`,
    },
  }

  const swiperParamsTaxonsPacksM: SwiperOptions = {
    ...swiperParamsMobile,
    navigation: {
      nextEl: `#swiper-taxonPack${universeSuffix}__next`,
      prevEl: `#swiper-taxonPack${universeSuffix}__prev`,
    },
  }

  const swiperParamsTaxonsPacksD: SwiperOptions = {
    ...swiperParamsDesktop,
    breakpoints: {
      768: {
        slidesPerView: 2,
      },
      1024: {
        slidesPerView:
          isTaxonsChildren && taxons.items[0].children.length < 4
            ? taxons.items[0].children.length
            : 4,
      },
      1680: {
        slidesPerView:
          isTaxonsChildren && taxons.items[0].children.length < 6
            ? taxons.items[0].children.length
            : 6,
      },
    },
    navigation: {
      nextEl: `#swiper-taxonPack${universeSuffix}__next`,
      prevEl: `#swiper-taxonPack${universeSuffix}__prev`,
    },
  }

  const swiperParamsProductsM: SwiperOptions = {
    ...swiperParamsMobile,
    navigation: {
      nextEl: `#swiper-products${universeSuffix}__next`,
      prevEl: `#swiper-products${universeSuffix}__prev`,
    },
  }

  const swiperParamsProductsD: SwiperOptions = {
    ...swiperParamsDesktop,
    breakpoints: {
      768: {
        slidesPerView: 2,
      },
      1024: {
        slidesPerView:
          carouselProducts && carouselProducts.length < 4
            ? carouselProducts.length
            : 4,
      },
      1680: {
        slidesPerView:
          carouselProducts && carouselProducts.length < 6
            ? carouselProducts.length
            : 6,
      },
    },
    navigation: {
      nextEl: `#swiper-products${universeSuffix}__next`,
      prevEl: `#swiper-products${universeSuffix}__prev`,
    },
  }

  useEffect(() => {
    getTaxons()
    getPackTaxons()
  }, [])

  const SwiperTaxons = ({ params }) => (
    <Swiper {...params}>
      {isTaxonsChildren &&
        taxons.items[0].children.map((taxon, index) => {
          const limit = universeSuffix
            ? (getProp('taxon_number_limit') as any)
            : taxonLimit
          if (index < prismicText(limit)) {
            return (
              <SwiperSlide key={index}>
                <DiscoverCard
                  isCarousel
                  url={'/shop'}
                  name={taxon.name}
                  taxonCat={taxon.slug}
                  img={
                    taxon?.images && taxon?.images?.length > 0
                      ? taxon?.images[0]
                      : ''
                  }
                  ratio={aspectRatio}
                  thumbWidth={thumbWidth}
                />
              </SwiperSlide>
            )
          }
        })}
    </Swiper>
  )

  const SwiperProducts = ({ params }) => (
    <Swiper {...params}>
      {carouselProducts?.map((carousel_product, index) => {
        return (
          <SwiperSlide key={index}>
            <DiscoverCard
              name={prismicText(carousel_product?.title)}
              productPrice={prismicText(carousel_product?.price)}
              url={
                carousel_product?.link?.url ? carousel_product?.link?.url : '/'
              }
              img={carousel_product?.image?.url}
              isProduct
              isCarousel
              ratio={aspectRatio}
            />
          </SwiperSlide>
        )
      })}
    </Swiper>
  )

  const SwiperTaxonPacks = ({ params }) => (
    <Swiper {...params}>
      {typeof taxonPack.items !== 'undefined' &&
        taxonPack.items.length > 0 &&
        taxonPack.items?.map((taxon) => {
          return (
            <SwiperSlide key={taxon.code}>
              <DiscoverCard
                isCarousel
                url={'/packs'}
                name={taxon.name}
                taxonCat={taxon.code}
                img={
                  taxon?.images && taxon?.images?.length > 0
                    ? taxon?.images[0]
                    : ''
                }
                ratio={aspectRatio}
              />
            </SwiperSlide>
          )
        })}
    </Swiper>
  )

  return (
    <div className={classes.background}>
      <section className={`section-discover section__spacer ${classes.root}`}>
        <Container maxWidth={'xl'} className={classes.container}>
          {getProp('enable_taxon_product') && (
            <div
              style={{
                order: (getProp('position_product_category') as number) || 1,
              }}
            >
              {isPrismicText(getProp('title_universe') as any) && (
                <div className={`section-discover__head ${classes.mainTitle}`}>
                  <Typography
                    variant={'h2'}
                    component={'h2'}
                    color={'primary'}
                    className={`section-discover__title ${classes.title}`}
                  >
                    {prismicText(getProp('title_universe') as any)}
                  </Typography>
                </div>
              )}
              {(getProp('subtitle_universe1') as any[])?.length !== 0 && (
                <div
                  className={clsx(
                    'section-discover__subtext',
                    'section-discover__subtext--categories',
                    classes.subtitle
                  )}
                >
                  <Typography
                    variant={'h2'}
                    color={'textPrimary'}
                    className={`section-discover__subtitle ${classes.title}`}
                  >
                    <CMSText
                      data={getProp('subtitle_universe1') as any}
                      asText
                    />
                  </Typography>
                  <Typography
                    variant={'body1'}
                    color={'textPrimary'}
                    className={`section-discover__desc ${classes.title}`}
                  >
                    {' '}
                    <CMSText
                      data={getProp('paragraph_universe1') as any}
                      asText
                    />
                  </Typography>
                </div>
              )}
              {getProp('univers_layout') ? (
                <div className={clsx('fullwidth', classes.fullwidth)}>
                  <div
                    className={
                      isArrows ? classes.swiperArrows : classes.swiperNoArrows
                    }
                  >
                    <>
                      {isArrows && (
                        <>
                          <Button
                            className={classes.swiperArrow}
                            id={`swiper-taxon${universeSuffix}__prev`}
                          >
                            <KeyboardArrowLeft />
                          </Button>
                          <Button
                            className={classes.swiperArrow}
                            id={`swiper-taxon${universeSuffix}__next`}
                          >
                            <KeyboardArrowRight />
                          </Button>
                        </>
                      )}
                      {isMobile ? (
                        <SwiperTaxons params={swiperParamsTaxonsM} />
                      ) : (
                        <SwiperTaxons params={swiperParamsTaxonsD} />
                      )}
                    </>
                  </div>
                </div>
              ) : (
                <Grid container spacing={3} justify={'center'}>
                  {taxons?.loading !== true &&
                  taxons?.items &&
                  taxons?.items[0]?.children?.length > 0
                    ? taxons.items[0].children.map((taxon, index) => {
                        const limit = universeSuffix
                          ? (getProp('taxon_number_limit') as any)
                          : taxonLimit
                        if (index < prismicText(limit)) {
                          return (
                            <Grid
                              item
                              key={index}
                              xs={6}
                              lg={isFiveColumns ? 2 : gridItemWidth}
                              className={clsx(
                                'section-discover__col',
                                `section-discover__col--${taxon.slug}`,
                                isFiveColumns ? classes.md5 : classes.col,
                                isMobileColumn &&
                                  'section-discover__col--mobile'
                              )}
                            >
                              <>
                                <DiscoverCard
                                  url={'/shop'}
                                  name={taxon.name}
                                  taxonCat={taxon.slug}
                                  img={
                                    taxon?.images && taxon?.images?.length > 0
                                      ? taxon?.images[0]
                                      : ''
                                  }
                                  ratio={aspectRatio}
                                  thumbWidth={thumbWidth}
                                />
                              </>
                            </Grid>
                          )
                        }
                      })
                    : [0, 1, 2].map((index) => {
                        return (
                          <Grid
                            item
                            key={index}
                            xs={6}
                            lg={isFiveColumns ? 2 : gridItemWidth}
                          >
                            <div>
                              <Typography
                                variant={'h3'}
                                color={'primary'}
                                className={`discover__title ${classes.taxonTitle}`}
                              >
                                <Skeleton variant={'text'} />
                              </Typography>
                              <div data-ratio={aspectRatio}>
                                <Skeleton
                                  variant="rect"
                                  className={classes.skeletonImg}
                                />
                              </div>
                            </div>
                          </Grid>
                        )
                      })}
                </Grid>
              )}
            </div>
          )}
          {getProp('enable_products_carousel') && (
            <div
              style={{
                order: (getProp('position_product_carrousel') as number) || 2,
              }}
            >
              {(getProp('subtitle_universe3') as any[])?.length !== 0 && (
                <div
                  className={`section-discover__subtext--products ${classes.subtitle}`}
                >
                  <Typography
                    variant={'h2'}
                    color={'textPrimary'}
                    className={`section-discover__subtitle ${classes.title}`}
                  >
                    <CMSText data={getProp('subtitle_universe3') as any} />
                  </Typography>
                  <Typography
                    variant={'body1'}
                    color={'textPrimary'}
                    className={`section-discover__desc ${classes.title}`}
                  >
                    {' '}
                    {prismicText(getProp('paragraph_universe3') as any)}
                  </Typography>
                </div>
              )}
              {getProp('univers_layout') ? (
                <div className={clsx('fullwidth', classes.fullwidth)}>
                  <div
                    className={
                      isArrows ? classes.swiperArrows : classes.swiperNoArrows
                    }
                  >
                    <>
                      {isArrows && (
                        <>
                          <Button
                            className={classes.swiperArrow}
                            id={`swiper-products${universeSuffix}__prev`}
                          >
                            <KeyboardArrowLeft />
                          </Button>
                          <Button
                            className={classes.swiperArrow}
                            id={`swiper-products${universeSuffix}__next`}
                          >
                            <KeyboardArrowRight />
                          </Button>
                        </>
                      )}
                      {isMobile ? (
                        <SwiperProducts params={swiperParamsProductsM} />
                      ) : (
                        <SwiperProducts params={swiperParamsProductsD} />
                      )}
                    </>
                  </div>
                </div>
              ) : (
                <Grid container spacing={3} justify={'center'}>
                  {carouselProducts?.map((carousel_product, index) => {
                    return (
                      <Grid
                        item
                        key={index}
                        xs={6}
                        lg={isFiveColumns ? 2 : gridItemWidth}
                        className={clsx(
                          isFiveColumns ? classes.md5 : classes.col,
                          'section-discover__col'
                        )}
                      >
                        <DiscoverCard
                          name={prismicText(carousel_product?.title)}
                          productPrice={prismicText(carousel_product?.price)}
                          url={carousel_product?.link?.url || '/'}
                          img={carousel_product?.image?.url}
                          isProduct
                          ratio={aspectRatio}
                        />
                      </Grid>
                    )
                  })}
                </Grid>
              )}
            </div>
          )}
          {isPack && getProp('enable_taxon_pack') && (
            <div
              style={{
                order: (getProp('position_pack_category') as number) || 3,
              }}
            >
              {(getProp('subtitle_universe1') as any[])?.length !== 0 && (
                <div
                  className={clsx(
                    'section-discover__subtext',
                    'section-discover__subtext--packs',
                    classes.subtitle
                  )}
                >
                  <Typography
                    variant={'h2'}
                    color={'textPrimary'}
                    className={`section-discover__subtitle ${classes.title}`}
                  >
                    <CMSText data={getProp('subtitle_universe2') as any} />
                  </Typography>
                  <Typography
                    variant={'body1'}
                    color={'textPrimary'}
                    className={`section-discover__desc ${classes.title}`}
                  >
                    {prismicText(getProp('paragraph_universe2') as any)}
                  </Typography>
                </div>
              )}
              {getProp('univers_layout') ? (
                <div className={clsx('fullwidth', classes.fullwidth)}>
                  <div
                    className={
                      isArrows ? classes.swiperArrows : classes.swiperNoArrows
                    }
                  >
                    <>
                      {isArrows && (
                        <>
                          <Button
                            className={classes.swiperArrow}
                            id={`swiper-taxonPack${universeSuffix}__prev`}
                          >
                            <KeyboardArrowLeft />
                          </Button>
                          <Button
                            className={classes.swiperArrow}
                            id={`swiper-taxonPack${universeSuffix}__next`}
                          >
                            <KeyboardArrowRight />
                          </Button>
                        </>
                      )}
                      {isMobile ? (
                        <SwiperTaxonPacks params={swiperParamsTaxonsPacksM} />
                      ) : (
                        <SwiperTaxonPacks params={swiperParamsTaxonsPacksD} />
                      )}
                    </>
                  </div>
                </div>
              ) : (
                <Grid container spacing={3} justify={'center'}>
                  {taxonPack?.loading !== true &&
                  taxonPack?.items &&
                  taxonPack.items?.length > 0
                    ? taxonPack.items?.map((taxon, index) => {
                        const packLimit = getProp(
                          'pack_taxon_limit_number'
                        ) as any
                        if (index < parseInt(prismicText(packLimit))) {
                          return (
                            <Grid
                              item
                              key={taxon.code}
                              xs={6}
                              lg={isFiveColumns ? 2 : gridItemWidth}
                              className={
                                isFiveColumns ? classes.md5 : classes.col
                              }
                            >
                              <DiscoverCard
                                url={'/packs'}
                                name={taxon.name}
                                taxonCat={taxon.code}
                                img={
                                  taxon?.images && taxon?.images?.length > 0
                                    ? taxon?.images[0]
                                    : ''
                                }
                                ratio={aspectRatio}
                              />
                            </Grid>
                          )
                        }
                      })
                    : [0, 1, 2].map((index) => {
                        return (
                          <Grid
                            item
                            key={index}
                            xs={6}
                            lg={isFiveColumns ? 2 : gridItemWidth}
                          >
                            <div>
                              <Typography
                                variant={'h3'}
                                color={'primary'}
                                className={`discover__title ${classes.taxonTitle}`}
                              >
                                <Skeleton variant={'text'} />
                              </Typography>
                              <div data-ratio={aspectRatio}>
                                <Skeleton
                                  variant="rect"
                                  className={classes.skeletonImg}
                                />
                              </div>
                            </div>
                          </Grid>
                        )
                      })}
                </Grid>
              )}
            </div>
          )}
          {isPrismicText(getProp('univers_cta_label') as any) &&
            (getProp('univers_cta_link') as any)?.url && (
              <div className={clsx('section-discover__footer', classes.footer)}>
                <Link href={(getProp('univers_cta_link') as any)?.url} scroll>
                  <a
                    className={clsx('discover__cta', classes.cardLink)}
                    onClick={() =>
                      trackEvent({
                        category: 'HomePage',
                        href: `${(getProp('univers_cta_link') as any)?.url}`,
                        action: `Click on ${prismicText(
                          getProp('univers_cta_label') as any
                        )}`,
                        name: `Universe Section to ${
                          (getProp('univers_cta_link') as any)?.url
                        }`,
                      })
                    }
                  >
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      className="section-univers__cta"
                      component={'span'}
                    >
                      {prismicText(getProp('univers_cta_label') as any)}
                    </Button>
                  </a>
                </Link>
              </div>
            )}
        </Container>
      </section>
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(Discover)
